import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { formCardClass } from '../../utils/formCardClass';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { TableActionLink } from '../../components/ui/TableActionLink';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  createTeam,
  deleteTeam,
  listTeams,
  TeamManagement,
  updateTeam,
} from '../../services/teamsApi';

export function TeamsAdminPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('access-control.teams');
  const [teams, setTeams] = useState<TeamManagement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', isActive: true });

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTeams({ page: targetPage, pageSize });
      setTeams(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar equipes.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm({ name: '', description: '', isActive: true });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    try {
      if (editingId) {
        await updateTeam(editingId, form);
        toast.success('Equipe atualizada.');
      } else {
        await createTeam(form);
        toast.success('Equipe criada.');
      }
      resetForm();
      await loadData(page);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar equipe.'));
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir equipe',
        message: 'Excluir esta equipe? Os vínculos de membros também serão removidos.',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deleteTeam(id);
      toast.success('Equipe excluída.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir equipe.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageShell>
      <PageHeader
        title="Equipes"
        description="Organize pessoas em equipes para atribuição de tarefas e responsabilidades."
      />

      {canWrite ? (
        <form onSubmit={(event) => void handleSubmit(event)} className={formCardClass(!!editingId)}>
          <div className="erp-card-body space-y-3">
            <h2 className="font-semibold">{editingId ? 'Editar equipe' : 'Nova equipe'}</h2>
            <div className="erp-form-grid md:grid-cols-3">
              <input
                className="erp-input-compact"
                placeholder="Nome"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
              <input
                className="erp-input-compact"
                placeholder="Descrição"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
              <label className="erp-label-inline">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                Ativa
              </label>
            </div>
            <FormActionBar showCancel={!!editingId} onCancel={resetForm} cancelLabel="Cancelar">
              <button type="submit" className="erp-btn-primary">
                Salvar
              </button>
            </FormActionBar>
          </div>
        </form>
      ) : null}

      <div className="erp-table-wrap">
        {loading ? (
          <p className="erp-loading">Carregando...</p>
        ) : (
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Membros</th>
                  <th>Chefes</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {teams.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-secondary-600">
                      Nenhuma equipe cadastrada.
                    </td>
                  </tr>
                ) : (
                  teams.map((team) => (
                    <tr key={team.id}>
                      <td>
                        <div className="font-medium text-secondary-900">{team.name}</div>
                        {team.description ? (
                          <div className="text-xs text-secondary-500 line-clamp-1">{team.description}</div>
                        ) : null}
                      </td>
                      <td>{team.memberCount}</td>
                      <td>{team.bossNames?.length ? team.bossNames.join(', ') : '—'}</td>
                      <td>
                        <ActiveStatusBadge active={team.isActive} feminine />
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <TableActionLink to={`/admin/teams/${team.id}/members`}>Membros</TableActionLink>
                          {canWrite ? (
                            <>
                              <EditIconButton
                                label="Editar equipe"
                                onClick={() => {
                                  setEditingId(team.id);
                                  setForm({
                                    name: team.name,
                                    description: team.description,
                                    isActive: team.isActive,
                                  });
                                }}
                              />
                              <DeleteIconButton
                                label="Excluir equipe"
                                onClick={() => void handleDelete(team.id)}
                              />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
